/* global window */

import * as firebase from 'firebase'

import Logger from '../Logger'

export default class MessagingController {

  constructor(userId) {
    this.userId = userId
    this.messaging = firebase.messaging()
  }

  bindTokenEvents = () => {
    this.messaging.onMessage(this.handleMessage)
    this.messaging.onTokenRefresh(this.handleTokenRefresh)
  }
  
  handleTokenRefresh = () => {
    this.syncToken()
  }

  handleMessage = (payload) => {
    Logger.info('Foreground message received', payload)
    if (payload && payload.data) {
      if (payload.data.notificationType === 'message') {
        this.handleNewChatMessageNotification(payload.data)
      }
      else
      if (payload.data.notificationType === 'newEvent') {
        this.handleNewEventNotification(payload.data)
      }
      else
      if (payload.data.notificationType === 'jobsSummary') {
        this.handleJobsSummaryNotification(payload.data)
      }
    }
  }

  handleNewChatMessageNotification = (data) => {
    // Check we are not in the messages tab to show the notification.
    if (!window.location.href.endsWith('app/messages')) {
      const options = {
        body: data.body,
        icon: '/notification.png'
      }
      this.showNotification(data.title, options)
    }
  }

  handleNewEventNotification = (data) => {
    const options = {
      body: data.body,
      icon: '/notification.png'
    }
    this.showNotification(data.title, options)
  }

  handleJobsSummaryNotification = (data) => {
    const options = {
      body: data.body,
      icon: '/notification.png'
    }
    this.showNotification(data.title, options)
  }

  showNotification = (title, options) => {
    this.notification = new window.Notification(title, options)
    window.setTimeout(() => {
      this.notification.close()
    }, (options.timeout || 5000))
  }

  syncToken = () => {
    // Get Instance ID token. Initially this makes a network call, once retrieved
    // subsequent calls to getToken will return from cache.
    this.messaging.getToken()
      .then(fcmToken => {
        Logger.info(`FCM Token: '${fcmToken}'`)
        // Send Instance ID token to app server.
        return this.sendTokenToServer(this.userId, fcmToken)
      })
      .catch(error => {
        Logger.error('Unable to retrieve refreshed token ', error)
        return false
      })
  }

  getDeviceKeyOrNew = (userId, current) => {
    // If this device is not synced then add a new key for this device.
    if (!current || !current.token) {
      return Promise.resolve(
        firebase.database().ref(`/users/${userId}/fcmTokens/`).push().key
      )
    }
    // Search for a key with the current token so we can do an update.
    return firebase.database()
      .ref(`/users/${userId}/fcmTokens/`)
      .orderByChild('token')
      .equalTo(current.token)
      .once('value')
      .then(snapshot => {
        Logger.info(JSON.stringify(snapshot))
        const value = snapshot && snapshot.val()
        if (value) {
          const keys = Object.keys(value)
          if (keys.length > 0) {
            return keys[0]
          }
        }
        // Not found
        return firebase.database()
          .ref(`/users/${userId}/fcmTokens/`).push().key
      })
  }

  /**
   * Attempts to save the FCM notification token to the app realtime DB.
   * 
   * Returns true if the db operation succeeded, false otherwise.
   */
  sendTokenToServer = (userId, newToken) => {
    const current = this.getFcmTokenInServer()
    if (!current || current.userId !== userId
      || current.token !== newToken) {
      return this.getDeviceKeyOrNew(userId, current)
        .then(key => {
          // Handle the case of blocked permission, which will
          // remove the FCM entry. 
          const updated = newToken
            ? {
              token: newToken,
              updated: firebase.database.ServerValue.TIMESTAMP
            }
            : null
          return firebase.database()
            .ref(`/users/${userId}/fcmTokens/${key}`)
            .set(updated)
            .then(() => {
              return this.setFcmTokenInServer(userId, newToken)
            })
            .catch(error => {
              Logger.error(null, error)
              return false
            })
        })
    }
    return true
  }

  /**
   * Returns the token we have for this user in the database.
   * This is saved in the browser's local storage. Use case is to update it's
   * value on token refreshes or user removing/updating permissions.
   */
  getFcmTokenInServer = () => {
    const serializedValue = window.localStorage.getItem('FcmTokenInServer')
    if (serializedValue) {
      return JSON.parse(serializedValue)
    }
    return null
  }

  setFcmTokenInServer = (userId, token) => {
    try {
      const entry = JSON.stringify({ userId, token })
      window.localStorage.setItem('FcmTokenInServer', entry)
    }
    catch (error) {
      Logger.error('Error setting local storage value', error)
    }
  }

  /**
   * On blocked notifications, let the server know
   * that it shouldn't send messages to this user.
   * 
   * The use case here is a user that allowed
   * notifications the first time, but later disabled them.
   * Update takes place of page refresh
   */
  sendBlockedPermissionToServer = (userId) => {
    this.sendTokenToServer(userId, null)
  }

  initialize() {
    const messagingSenderId = firebase.app().options.messagingSenderId
    // Register the service worker sending the messagingSenderId for firebase initialization.
    // Check browser supports service workers.
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('firebase-messaging.js?messagingSenderId=' + messagingSenderId)
        .then(registration => {
          this.messaging.useServiceWorker(registration)
        })
        .then(() => {
          // Request permission asks the user to allow/block notifications.
          // On subsequent calls to this method, the cached response is returned.
          return this.messaging.requestPermission()
        })
        .then(() => {
          Logger.info('Notification permission granted.')
          this.bindTokenEvents()
          this.syncToken()
        })
        .catch(err => {
          Logger.info('Unable to get permission to notify.', err)
          this.sendBlockedPermissionToServer(this.userId)
          // set granted permission to false in the DB
        })
    }
  }

}



// WEBPACK FOOTER //
// ./src/infra/messaging/MessagingController.js